import {
  Sprite,
  SpriteMaterial,
  TextureLoader,
  Scene
} from 'three';
import { GAME_CONSTANTS, PLAYER_CONSTANTS } from '../constants.tsx';
import { CollisionManager } from './CollisionManager.ts';

type Direction = 'up' | 'down' | 'left' | 'right';

export class Pawn {
  public sprite!: Sprite;
  public originX: number;
  public originY: number;

  private materials!: Record<Direction, SpriteMaterial>;
  private currentFrame = 0;
  private lastFrameChangeTime = 0;

  private collisionManager: CollisionManager | null = null;
  private playerSprite: Sprite | null = null;
  private state: 'walking' | 'resting' = 'resting';
  private currentDirection: Direction = 'down';
  private stateTimer: number;

  private static readonly SCALE = 48;
  private static readonly SPEED = 1.2;
  private static readonly FRAME_TIME = 150;
  private static readonly MAX_WANDER = 150;
  private static readonly TOTAL_FRAMES = PLAYER_CONSTANTS.COLUMNS * PLAYER_CONSTANTS.ROWS;

  constructor(startX: number, startY: number, collisionManager?: CollisionManager, playerSprite?: Sprite) {
    this.originX = startX;
    this.originY = startY;
    this.collisionManager = collisionManager ?? null;
    this.playerSprite = playerSprite ?? null;
    this.stateTimer = 1000 + Math.random() * 2000;
    this.loadTextures();
    this.createSprite(startX, startY);
  }

  private loadTextures() {
    const loader = new TextureLoader();
    const base = '/assets/players/dogs/oliver';

    this.materials = {} as Record<Direction, SpriteMaterial>;

    const fileMap: Record<Direction, string> = {
      up: 'walk_down',
      down: 'walk_up',
      left: 'walk_left',
      right: 'walk_right',
    };

    for (const dir of ['up', 'down', 'left', 'right'] as Direction[]) {
      const texture = loader.load(`${base}/${fileMap[dir]}.png`);
      texture.offset.set(0, 0);
      texture.repeat.set(GAME_CONSTANTS.FRAME_WIDTH, GAME_CONSTANTS.FRAME_HEIGHT);
      this.materials[dir] = new SpriteMaterial({ map: texture });
    }
  }

  private createSprite(x: number, y: number) {
    this.sprite = new Sprite(this.materials.down);
    this.sprite.position.set(x, y, PLAYER_CONSTANTS.INIT_Z);
    this.sprite.scale.set(Pawn.SCALE, Pawn.SCALE, Pawn.SCALE);
  }

  public addToScene(scene: Scene) {
    scene.add(this.sprite);
  }

  public update(deltaTime: number) {
    this.stateTimer -= deltaTime;

    if (this.state === 'resting') {
      if (this.stateTimer <= 0) {
        this.startWalking();
      }
    } else {
      this.animateFrame();
      this.move(deltaTime);
      if (this.stateTimer <= 0) {
        this.startResting();
      }
    }
  }

  private startWalking() {
    const dir = this.pickDirection();
    if (!dir) {
      this.stateTimer = 500 + Math.random() * 1000;
      return;
    }
    this.currentDirection = dir;
    this.state = 'walking';
    this.stateTimer = 2000 + Math.random() * 2000;
    this.sprite.material = this.materials[this.currentDirection];
    this.currentFrame = 0;
    this.lastFrameChangeTime = 0;
  }

  private startResting() {
    this.state = 'resting';
    this.stateTimer = 1500 + Math.random() * 2500;
    const mat = this.materials[this.currentDirection];
    this.sprite.material = mat;
    mat.map!.offset.set(0, 0);
    mat.map!.repeat.set(GAME_CONSTANTS.FRAME_WIDTH, GAME_CONSTANTS.FRAME_HEIGHT);
  }

  private pickDirection(): Direction | null {
    const allDirs: Direction[] = ['up', 'down', 'left', 'right'];
    const available = this.collisionManager
      ? allDirs.filter(d => !this.collisionManager!.checkPawnCollision(this.sprite, d, this.playerSprite ?? undefined))
      : allDirs;

    if (available.length === 0) return null;

    const dx = this.originX - this.sprite.position.x;
    const dy = this.originY - this.sprite.position.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > Pawn.MAX_WANDER) {
      const preferred = Math.abs(dx) > Math.abs(dy)
        ? (dx > 0 ? 'right' : 'left')
        : (dy > 0 ? 'up' : 'down');
      if (available.includes(preferred)) return preferred;
    }

    return available[Math.floor(Math.random() * available.length)];
  }

  private animateFrame() {
    const now = performance.now();
    if (now - this.lastFrameChangeTime > Pawn.FRAME_TIME) {
      this.currentFrame = (this.currentFrame + 1) % Pawn.TOTAL_FRAMES;
      this.lastFrameChangeTime = now;

      const col = this.currentFrame % PLAYER_CONSTANTS.COLUMNS;
      const row = Math.floor(this.currentFrame / PLAYER_CONSTANTS.COLUMNS);
      const mat = this.materials[this.currentDirection];
      mat.map!.offset.x = col * GAME_CONSTANTS.FRAME_WIDTH;
      mat.map!.offset.y = 1 - (row + 1) * GAME_CONSTANTS.FRAME_HEIGHT;
      mat.map!.repeat.set(GAME_CONSTANTS.FRAME_WIDTH, GAME_CONSTANTS.FRAME_HEIGHT);
    }
  }

  private move(deltaTime: number) {
    if (this.collisionManager?.checkPawnCollision(this.sprite, this.currentDirection, this.playerSprite ?? undefined)) {
      this.startResting();
      return;
    }
    const speed = Pawn.SPEED * (deltaTime / 16.67);
    switch (this.currentDirection) {
      case 'up': this.sprite.position.y += speed; break;
      case 'down': this.sprite.position.y -= speed; break;
      case 'left': this.sprite.position.x -= speed; break;
      case 'right': this.sprite.position.x += speed; break;
    }
  }

  public moveWithWorld(dx: number, dy: number) {
    this.sprite.position.x += dx;
    this.sprite.position.y += dy;
    this.originX += dx;
    this.originY += dy;
  }
}
